import React, { useEffect, useRef } from 'react'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { InView } from 'react-intersection-observer'

import { routes } from '@sketch/constants'

import AspectRatioGrid from 'components/AspectRatioGrid'
import { Breakpoint, Table } from '@sketch/components'
import { UpsellBanner } from 'modules/designSystems/components/UpsellBanner'

import useUserProfile from 'modules/user/useUserProfile'
import { useFlag } from 'containers/flags'

import {
  ShareListItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'

import { ResponsiveValues } from '@sketch/global-styles'

import LoadingState from '../LoadingState'
import ShareListItemDrag from '../ShareListItemDrag'
import EmptyState from 'modules/shares/components/EmptyState'

import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
  ConnectedDocumentItem,
  RenderDropdown,
} from '../DocumentItem'

import {
  ActionTableCell,
  Footer,
  TableWrapper,
  FocusLink,
  HeaderTableCell,
} from './DocumentItemsLayout.styles'
import { clamp } from '@sketch/utils'

type Share = ShareListItemFragment

export type { RenderDropdown } from '../DocumentItem'

interface CustomColumns {
  customColumns?: ResponsiveValues<number>
}

interface DocumentItemsLayoutProps extends CustomColumns {
  shares: Share[]
  loading: boolean
  showProjectName: boolean
  onLoadMore: () => Promise<any>
  totalShareCount: number
  renderDropdown: RenderDropdown
  action?: (share: Share) => (() => void) | 'link'
  header?: React.ReactNode
  search?: string
  canUseDesignSystemManager?: boolean
}

export const TABLE_HEADER = [
  { label: 'Document', customCell: HeaderTableCell },
  { label: 'Project', customCell: HeaderTableCell },
  { label: 'Last Updated', customCell: HeaderTableCell },
  { label: 'Updated By', customCell: HeaderTableCell },
  { label: 'Action', customCell: ActionTableCell },
]

const COLUMNS: ResponsiveValues<number> = [2, 2, 3, 3, 3, 4, 5, 6]
const GUTTER: ResponsiveValues<number> = [16, 16, 16, 16, 40, 40, 40]
const VERTICAL_SPACE: ResponsiveValues<number> = [24, 24, 24, 24, 40, 40, 40]
const PAGE_SIZE = 20

const buildPlaceholderArray = (remainingSharesToLoad: number) =>
  Array(clamp(remainingSharesToLoad, 0, PAGE_SIZE))

// Auxiliary components
const GridLayout: React.FC<CustomColumns> = ({ children, customColumns }) => (
  <AspectRatioGrid
    columns={customColumns ?? COLUMNS}
    verticalRowSpace={VERTICAL_SPACE}
    gutterSize={GUTTER}
  >
    {children}
  </AspectRatioGrid>
)

const DocumentItemsLayout: React.FC<DocumentItemsLayoutProps> = props => {
  const {
    shares,
    showProjectName,
    onLoadMore,
    loading,
    action: actionCreator = () => 'link',
    totalShareCount,
    header,
    renderDropdown,
    search,
    canUseDesignSystemManager,
    customColumns,
  }: typeof props = props
  const isDesignSystemsOn = useFlag('design-systems')

  const history = useHistory()

  const isWorkspaceLibrariesRoute = useRouteMatch({
    path: routes.WORKSPACE_LIBRARIES.template(),
    exact: true,
  })

  // Make sure the onLoadMore Reference is always the latest
  const onLoadMoreRef = useRef<() => Promise<any>>(onLoadMore)
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  if (loading && shares.length === 0) {
    return <LoadingState />
  }

  const renderGridItem = (share: ShareListItemFragment, isMobile: boolean) => {
    const action = actionCreator(share)

    if (action !== 'link') {
      return (
        <ShareListItemDrag
          key={share.identifier}
          shareId={share.identifier}
          disabled={isMobile || !share.userCanUpdateSettings}
        >
          <ConnectedDocumentItem
            key={share.identifier}
            share={share}
            renderDropdown={renderDropdown}
            presentation="grid"
            showProjectName={showProjectName}
            onClick={() => action()}
          />
        </ShareListItemDrag>
      )
    }

    return (
      <ShareListItemDrag
        key={share.identifier}
        shareId={share.identifier}
        disabled={isMobile || !share.userCanUpdateSettings}
      >
        <FocusLink
          draggable={false}
          to={{
            pathname: routes.SHARE_VIEW.create({ shareID: share.identifier }),
            state: { componentsView: true },
          }}
        >
          <ConnectedDocumentItem
            key={share.identifier}
            share={share}
            renderDropdown={renderDropdown}
            presentation="grid"
            showProjectName={showProjectName}
            userIdentifier={userIdentifier}
          />
        </FocusLink>
      </ShareListItemDrag>
    )
  }

  const renderTableItem = (share: ShareListItemFragment) => {
    const action = actionCreator(share)

    return (
      <ConnectedDocumentItem
        share={share}
        renderDropdown={renderDropdown}
        presentation="list"
        showProjectName={showProjectName}
        onClick={event => {
          if (action !== 'link') {
            action()
            return
          }

          const link = routes.SHARE_VIEW.create({
            shareID: share.identifier,
          })

          if (event.metaKey) {
            /* Simulate the link opening with meta key */
            window.open(link, '_blank')
          } else {
            history.push(link)
          }
        }}
        userIdentifier={userIdentifier}
      />
    )
  }

  const renderLayout = (
    isMobile: boolean,
    customColumns?: ResponsiveValues<number>
  ) => {
    const placeholders = buildPlaceholderArray(
      (totalShareCount || 0) - shares.length
    )

    // for design systems search: check if we're on the library route and if empty count for a special message
    if (
      isWorkspaceLibrariesRoute &&
      totalShareCount === 0 &&
      isDesignSystemsOn
    ) {
      return (
        <EmptyState
          search={search}
          title="No Libraries"
          description="No Libraries added yet."
          icon="link"
        />
      )
    }

    // Merge the items with the placeholders have them rendered together
    // This prevents the page from bumping when the placeholders are replaced
    // with actual items
    const sharesAndPlaceholders = [...shares, ...placeholders]

    // Create the InView listener warn when it starts showing the placeholders, this will load the next page
    const inViewListener =
      placeholders.length > 0 ? (
        <InView onChange={inView => inView && onLoadMoreRef.current?.()}>
          <span className="sr-only">Loading Placeholder</span>
        </InView>
      ) : null

    if (documentsLayout === 'LIST') {
      return (
        <TableWrapper $hideProjects={!showProjectName}>
          <Table
            header={TABLE_HEADER}
            items={sharesAndPlaceholders}
            renderItemKey={share =>
              // if it's not a share the table will generate a index based key
              typeof share === 'object' ? share.identifier : undefined
            }
            renderItem={item => {
              if (typeof item === 'object') {
                return renderTableItem(item)
              }

              return (
                <DocumentTableItemPlaceholder inViewListener={inViewListener} />
              )
            }}
            evenColumns={!isMobile}
          />
        </TableWrapper>
      )
    } else {
      return (
        <GridLayout customColumns={customColumns}>
          {sharesAndPlaceholders.map((item, index) => {
            if (typeof item === 'object') {
              return renderGridItem(item, isMobile)
            }

            return (
              <DocumentGridItemPlaceholder
                key={index}
                inViewListener={inViewListener}
              />
            )
          })}
        </GridLayout>
      )
    }
  }

  // if library, get the upsell banner
  const showUpsell = !canUseDesignSystemManager && isWorkspaceLibrariesRoute

  return (
    <>
      {header}
      {showUpsell && <UpsellBanner />}
      <Breakpoint on="sm">
        {matches => renderLayout(!matches, customColumns)}
      </Breakpoint>
      <Footer />
    </>
  )
}

export default DocumentItemsLayout
